import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Route, Plus, Trash2, X, Pencil } from 'lucide-react'
import { useAuth } from '../store/auth'
import { getPaths, createPath, updatePath, deletePath } from '../api/auth'
import { Badge } from '../components/Badge'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { SearchInput } from '../components/SearchInput'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import type { Path } from '../types'

const METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] as const

export function Paths() {
  const fmt = useDateFmt()
  const { user: me } = useAuth()
  const businessId = me!.businessId
  const qc = useQueryClient()

  const { data: paths = [], isLoading } = useQuery({
    queryKey: ['paths', businessId],
    queryFn: () => getPaths(businessId).then((r) => r.data),
  })

  const emptyForm = { method: 'GET' as typeof METHODS[number], url: '', description: '' }
  const [showForm, setShowForm] = useState(false)
  const [editTarget, setEditTarget] = useState<Path | null>(null)
  const [form, setForm] = useState(emptyForm)

  function openCreate() {
    setEditTarget(null)
    setForm(emptyForm)
    setShowForm(true)
  }

  function openEdit(p: Path) {
    setEditTarget(p)
    setForm({ method: p.method, url: p.url, description: p.description ?? '' })
    setShowForm(true)
  }

  function closeForm() {
    setShowForm(false)
    setEditTarget(null)
    setForm(emptyForm)
  }

  const create = useMutation({
    mutationFn: () => createPath({ businessId, ...form }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['paths'] }); setSelected(res.data); closeForm() },
  })

  const update = useMutation({
    mutationFn: () => updatePath(editTarget!.id, form),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['paths'] }); setSelected(res.data); closeForm() },
  })

  const [search, setSearch] = useState('')
  const filtered = paths.filter((p: Path) =>
    [p.method, p.url, p.description ?? ''].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  const [selected, setSelected] = useState<Path | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Path | null>(null)

  const del = useMutation({
    mutationFn: (id: string) => deletePath(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['paths'] })
      setDeleteTarget(null)
      setSelected(null)
    },
  })

  return (
    <div className="flex gap-6 h-full">
      {/* List */}
      <div className="flex-1 min-w-0">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-ink">Paths</h1>
            <p className="text-sm text-ink-dim mt-0.5">Define permission paths and assign them to roles</p>
          </div>
          <div className="flex items-center gap-2">
            <SearchInput value={search} onChange={setSearch} placeholder="Search paths" />
            <Button icon={Plus} onClick={openCreate}>New path</Button>
          </div>
        </div>

        <div className="table-wrap">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
          ) : paths.length === 0 ? (
            <EmptyState icon={Route} title="No paths yet" description="Paths define what endpoints a role can access" />
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-24">Method</th>
                  <th>URL</th>
                  <th className="hidden md:table-cell">Description</th>
                  <th className="hidden lg:table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p: Path) => (
                  <tr
                    key={p.id}
                    onClick={() => setSelected(p)}
                    className={`cursor-pointer ${selected?.id === p.id ? 'bg-paper-sunken' : ''}`}
                  >
                    <td>
                      <Badge variant={p.method}>{p.method}</Badge>
                    </td>
                    <td className="font-mono text-xs text-ink">{p.url}</td>
                    <td className="text-ink-dim hidden md:table-cell">{p.description ?? ''}</td>
                    <td className="text-ink-dim hidden lg:table-cell">{fmt(p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="w-80 shrink-0">
          <div className="overflow-hidden sticky top-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-paper-border">
              <h2 className="text-sm font-semibold text-ink">Path details</h2>
              <button onClick={() => setSelected(null)} className="text-ink-dim hover:text-ink transition-colors">
                <X size={15} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <p className="text-xs font-medium text-ink-dim mb-1">Method</p>
                <Badge variant={selected.method}>{selected.method}</Badge>
              </div>
              <div>
                <p className="text-xs font-medium text-ink-dim mb-1">URL</p>
                <p className="font-mono text-xs text-ink break-all">{selected.url}</p>
              </div>
              {selected.description && (
                <div>
                  <p className="text-xs font-medium text-ink-dim mb-1">Description</p>
                  <p className="text-sm text-ink">{selected.description}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-ink-dim mb-1">ID</p>
                <p className="font-mono text-xs text-ink-dim break-all">{selected.id}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-ink-dim mb-1">Created</p>
                <p className="text-sm text-ink">{fmt(selected.createdAt)}</p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-paper-border flex gap-2">
              <Button variant="secondary" icon={Pencil} onClick={() => openEdit(selected)} className="flex-1">
                Edit
              </Button>
              <Button variant="danger" icon={Trash2} onClick={() => setDeleteTarget(selected)} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <Modal title={editTarget ? `Edit path` : 'New path'} onClose={closeForm} size="sm">
          <div className="space-y-4">
            <Field label="Method *">
              <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v as typeof METHODS[number] }))}>
                {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </Select>
            </Field>
            <Field label="URL *">
              <input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="http://localhost:3000/*"
                className="field font-mono" />
            </Field>
            <Field label="Description">
              <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional"
                className="field" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={closeForm}>Cancel</Button>
              <Button
                onClick={() => editTarget ? update.mutate() : create.mutate()}
                disabled={!form.url}
                loading={create.isPending || update.isPending}
              >
                {editTarget ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete path"
          message={`Delete "${deleteTarget.method} ${deleteTarget.url}"? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}
    </div>
  )
}
